import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, Search } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, IconButton } from '../ui';
import { Brand, UserAvatar } from './Brand';
import { GlobalSearchBar } from './GlobalSearchBar';
import { AlertBadge } from '../../features/monitoring/components/AlertBadge';
import { OfflineStatus } from '../../features/offline';
import { displayName, roleLabel } from './navigation';

/** Frosted header: search on desktop; brand, search and account on mobile. */
export const TopBar: React.FC = () => {
  const { user, logout } = useAuth();
  const [searchOpen, setSearchOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  return (
    <>
      <header
        className="sticky top-0 z-30 border-b border-neutral-200/70 bg-white/80 font-ui backdrop-blur-xl backdrop-saturate-150"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        {/* Desktop */}
        <div className="hidden h-16 items-center px-8 lg:flex">
          <div className="w-full max-w-md">
            <GlobalSearchBar />
          </div>
          <div className="ml-auto flex items-center gap-1 pl-4">
            <OfflineStatus />
            <AlertBadge />
          </div>
        </div>

        {/* Mobile */}
        <div className="flex h-14 items-center justify-between gap-3 pl-4 pr-2 lg:hidden">
          <Link to="/dashboard" className="rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40">
            <Brand />
          </Link>
          <div className="flex items-center gap-1">
            <OfflineStatus size="lg" />
            <AlertBadge size="lg" />
            <IconButton label="Search" className="h-11 w-11 text-ink" onClick={() => setSearchOpen(true)}>
              <Search className="h-[22px] w-[22px]" strokeWidth={1.75} />
            </IconButton>
            <button
              type="button"
              onClick={() => setAccountOpen(true)}
              aria-label="Account"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full transition-transform active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
            >
              <UserAvatar user={user} size="sm" />
            </button>
          </div>
        </div>
      </header>

      <Dialog
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        label="Search"
        handle
        className="h-[92dvh] sm:h-auto sm:min-h-[420px] sm:max-w-lg"
      >
        <div className="flex min-h-0 flex-1 flex-col px-4 pb-4 sm:pt-5">
          <GlobalSearchBar
            inline
            autoFocus
            onNavigate={() => setSearchOpen(false)}
            trailing={
              <button
                type="button"
                onClick={() => setSearchOpen(false)}
                className="h-11 flex-shrink-0 px-2 text-[15px] text-primary-600"
              >
                Cancel
              </button>
            }
          />
        </div>
      </Dialog>

      <Dialog
        open={accountOpen}
        onClose={() => setAccountOpen(false)}
        label="Account"
        handle
        surface="muted"
        className="sm:max-w-sm"
      >
        <div className="px-4 pb-4 pt-2 sm:pt-5" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
          <div className="flex flex-col items-center py-4 text-center">
            <UserAvatar user={user} size="lg" />
            <p className="mt-3 text-[19px] tracking-[-0.02em] text-ink">{displayName(user)}</p>
            {user?.email && user.email !== displayName(user) && (
              <p className="mt-0.5 text-[14px] text-neutral-500">{user.email}</p>
            )}
            <p className="mt-2 rounded-full bg-white px-2.5 py-0.5 text-[12px] text-neutral-500">{roleLabel(user?.role)}</p>
          </div>
          <button
            type="button"
            onClick={() => {
              setAccountOpen(false);
              logout();
            }}
            className="mt-2 flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-white text-[15px] text-primary-600 transition-colors active:bg-neutral-50"
          >
            <LogOut className="h-[18px] w-[18px]" strokeWidth={1.75} />
            Log out
          </button>
        </div>
      </Dialog>
    </>
  );
};
